import ReportHeader from "../../event/components/report/ReportHeader";
import ReportSectionTitle from "../../../components/common/ReportSectionTitle";
import localize from "../../../common/localize";
import { getValueInCurrency } from "../../../common/utils";
import { getCurrencyFromSetup } from "../../../common/EntitiesManagement";
import { isSettled } from "./settlementReceipt";
import {
  filterSettlementDetails,
  EMPTY_SETTLEMENT_FILTERS,
} from "./settlementFilterUtils";

// Summary page for settlement reports: grand total settled value plus, per
// personal assigned, the number of distinct service orders and their settled
// value. Only settled details (Approved/Payed) are counted, matching the body.
export default function SettlementReportSummary({
  details,
  filters = EMPTY_SETTLEMENT_FILTERS,
}) {
  const currency = getCurrencyFromSetup();
  const settled = filterSettlementDetails(details, filters).filter(isSettled);

  const byPersonal = new Map();
  let grandTotal = 0;
  settled.forEach((item) => {
    const key = item?.personal?.id ?? item?.personal?.FullName ?? "unknown";
    if (!byPersonal.has(key)) {
      byPersonal.set(key, {
        name: item?.personal?.FullName ?? localize.no_data,
        events: new Set(),
        total: 0,
      });
    }
    const group = byPersonal.get(key);
    group.events.add(item?.event?.Id ?? item?.event?.Code ?? "unknown");
    group.total += item?.value ?? 0;
    grandTotal += item?.value ?? 0;
  });

  const rows = Array.from(byPersonal.values()).sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? ""),
  );
  const totalOrders = rows.reduce((sum, r) => sum + r.events.size, 0);

  const cellCount = { width: "160px", textAlign: "right", flexShrink: 0 };
  const cellValue = {
    width: "180px",
    textAlign: "right",
    whiteSpace: "nowrap",
    flexShrink: 0,
  };

  return (
    <div>
      <ReportHeader
        event={{}}
        customData={[
          localize.settlement_detail_title,
          localize.settlement_summary_title,
        ]}
      />
      <ReportSectionTitle title={localize.settlement_summary_title} />
      <div className="report_section_without_top_border">
        <div style={{ padding: "8px 12px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: "bold",
              borderBottom: "1px solid #000",
              paddingBottom: "4px",
              marginBottom: "8px",
            }}
          >
            <span className="report_little_text">
              {localize.settlement_total_settled_value}
            </span>
            <span className="report_little_text" style={{ textAlign: "right" }}>
              {getValueInCurrency(currency, grandTotal)}
            </span>
          </div>

          {settled.length > 0 ? (
            <>
              <div
                style={{
                  display: "flex",
                  fontWeight: "bold",
                  borderBottom: "1px solid #000",
                  paddingBottom: "2px",
                }}
              >
                <span className="report_little_text" style={{ flex: 1, minWidth: 0 }}>
                  {localize.personal}
                </span>
                <span className="report_little_text" style={cellCount}>
                  {localize.service_orders}
                </span>
                <span className="report_little_text" style={cellValue}>
                  {localize.settlement_total_settled_value}
                </span>
              </div>

              {rows.map((row, index) => (
                <div
                  key={index}
                  className="print-row"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    padding: "3px 0",
                    backgroundColor: index % 2 === 0 ? "#F3F4F6" : "#FFFFFF",
                  }}
                >
                  <span
                    className="report_little_text"
                    style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
                  >
                    {row.name}
                  </span>
                  <span className="report_little_text" style={cellCount}>
                    {row.events.size}
                  </span>
                  <span className="report_little_text" style={cellValue}>
                    {getValueInCurrency(currency, row.total)}
                  </span>
                </div>
              ))}

              <div
                style={{
                  display: "flex",
                  fontWeight: "bold",
                  borderTop: "1px solid #000",
                  marginTop: "5px",
                  paddingTop: "2px",
                }}
              >
                <span className="report_little_text" style={{ flex: 1, minWidth: 0 }}>
                  {localize.total_value}
                </span>
                <span className="report_little_text" style={cellCount}>
                  {totalOrders}
                </span>
                <span className="report_little_text" style={cellValue}>
                  {getValueInCurrency(currency, grandTotal)}
                </span>
              </div>
            </>
          ) : (
            <label className="report_little_text">{localize.no_data}</label>
          )}
        </div>
      </div>
    </div>
  );
}
